<template>
  <div class="performance-wrapper">
    <div class="title-n-buttons">
      <div style="display: flex; flex-direction: column">
        <h2 class="performance-title">
          {{ prettifyDate(form.$model.date, form.$model.time) || `performance ${index + 1}` }}
        </h2>
        <span v-if="form.$model.eventCode" class="scanner-id">
          Scanner ID: {{ form.$model.eventCode }}
        </span>
      </div>
      <div class="performance-buttons">
        <section style="margin-right: 16px">
          <v-tooltip bottom>
            <a
              v-if="!newEventModal && soldAtLeastOnce"
              slot="activator"
              class="not-selectable button-style reports-wrapper"
              target="_blank"
              :href="`/theater-organizations/${organizationSlug}/event/${form.$model.id}/report`"
              style="color: #db384c"
            >
              <img alt="Report" src="/assets/event-report.svg" />
            </a>
            Download the performance report
          </v-tooltip>
          <div
            v-if="form.$model.from > new Date().toISOString() || newEventModal"
            style="display: flex; gap: 4px"
          >
            <v-tooltip bottom>
              <button
                slot="activator"
                class="not-selectable button-style duplicate-wrapper"
                style="color: #0277bd"
                @click="$emit('duplicatePerformance', form.$model)"
              >
                <img
                  alt="Duplicate icon"
                  src="/assets/duplicate-performance.svg"
                  style="height: fit-content"
                />
              </button>
              Duplicate performance
            </v-tooltip>
            <v-tooltip bottom>
              <button
                class="not-selectable button-style delete-wrapper"
                :style="`color: ${soldAtLeastOnce ? '#979797' : '#DB384C'}`"
                :disabled="soldAtLeastOnce"
                @click="$emit('removePerformance', form.$model.id)"
              >
                <img
                  alt="Delete icon"
                  :src="`${
                    soldAtLeastOnce ? '/assets/delete-icon-disabled.svg' : '/assets/delete-icon.svg'
                  }`"
                />
              </button>
              Delete this performance
            </v-tooltip>
          </div>
          <v-tooltip bottom>
            <div slot="activator" class="event-applications">
              <img
                alt="applications"
                src="/assets/applications-open.svg"
                style="height: fit-content"
              />
              <v-switch
                v-model="form.$model.applicationsOpen"
                hide-details
                style="margin: 0"
                inset
              />
            </div>
            Available for purchasing
          </v-tooltip>
        </section>
        <span class="material-icons not-selectable" @click="expanded = !expanded">
          {{ expanded ? 'expand_less' : 'expand_more' }}
        </span>
      </div>
    </div>
    <div class="performance-details" :style="!expanded && 'display: none'">
      <div class="performance-date">
        <date-field
          v-model="form.$model.date"
          style="width: 100%"
          label="Date*"
          :validator="vs.date"
          :disabled="soldAtLeastOnce"
        />
        <time-field
          v-model="form.$model.time"
          style="width: 100%"
          label="Time*"
          :validator="vs.time"
          :disabled="soldAtLeastOnce"
        />
      </div>
      <select-field
        v-model="form.$model.performanceType"
        :disabled="soldAtLeastOnce"
        label="Type of performance*"
        :validator="vs.performanceType"
        :items="performanceTypes"
      />
      <select-field
        v-model="form.$model.venueId"
        :disabled="soldAtLeastOnce"
        :items="venues"
        item-render="name"
        item-select="id"
        label="Odeum"
        @change="
          () => form.$model.orderItemTemplateIds && (form.$model.orderItemTemplateIds.length = 0)
        "
      />
      <text-field
        v-if="
          venues
            .filter(v => !v.seats || !v.predefinedSections)
            .map(v => v.id)
            .includes(form.$model.venueId)
        "
        v-model.number="form.$model.audienceCapacity"
        type="number"
        step="1"
        label="Tickets available*"
        :validator="vs.audienceCapacity"
      />
      <div class="ticket-type-wrapper">
        <select-field-multiple
          v-model="form.$model.orderItemTemplateIds"
          :validator="vs.orderItemTemplateIds"
          :disabled="soldOut"
          style="width: 100%"
          label="Type of Tickets*"
          :items="
            ticketTypeItems.map(item => {
              return {
                ...item,
                disabled: orderItemTemplateIdsSoldAtLeastOnce.includes(item.id),
              }
            })
          "
          item-render="name"
          item-select="id"
        />
        <main-button
          flat
          style="height: fit-content"
          :disabled="soldOut"
          @click="handleOpenCreateTicketModal"
        >
          <span class="add-symbol">+</span>Create ticket
        </main-button>
      </div>
      <select-field-multiple
        v-if="displayPromoCodes()"
        key="name"
        v-model="promoCodeNames"
        :disabled="soldAtLeastOnce || promoCodesAreSelectable"
        style="width: 100%"
        label="Promo code(s) – Applicable only when a regular ticket is selected"
        :items="categorizePromoCodes"
        @change="item => handlePromoCodeClick(item)"
      />
      <select-field-multiple
        v-if="hasSomeTicketSalesChannelsAvailable"
        v-model="selectedTicketSellers"
        item-select="id"
        item-render="name"
        :disabled="soldAtLeastOnce"
        style="width: 100%"
        label="Ticket sellers"
        :items="ticketSellers"
        @change="item => handleTicketSellerClick(item)"
      />
      <p v-if="hasSomeTicketSalesChannelsAvailable && isTisakInTicketSellers" class="tisak-term">
        TISAK outlets will charge ticket buyers additional 1 EUR per ticket
      </p>
      <ticket-type-create-modal
        v-if="openCreateTicketModal"
        :organization-venues="venues"
        @close="handleOpenCreateTicketModal"
        @closeModal="handleOpenCreateTicketModal"
        @createTicket="handleCreateTicketType"
      />
    </div>
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="snackbar.message"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import TicketTypeCreateModal from '@/components/organizations/ticket-types/TicketTypeCreateModal.vue'
import { CREATE_ORGANIZATION_ORDER_ITEM_TEMPLATES } from '@/graphql/orderItemTemplate.graphql'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { EventPerformanceType } from '@/types'
import moment from 'moment'
import { tisakTicketSeller } from '@/utils'

export default {
  name: 'performance',
  components: {
    TicketTypeCreateModal,
    SimpleSnackbar,
  },
  props: {
    initialValues: {
      type: Object,
      default: () => {},
    },
    index: {
      type: Number,
      default: 0,
    },
    vs: {
      type: Object,
      default: () => {},
    },
    venues: {
      type: Array,
      default: () => [],
    },
    promoCodes: {
      type: Array,
      default: () => [],
    },
    ticketTypes: {
      type: Array,
      default: () => [],
    },
    soldOut: {
      type: Boolean,
      default: false,
    },
    soldAtLeastOnce: {
      type: Boolean,
      default: false,
    },
    orderItemTemplateIdsSoldAtLeastOnce: {
      type: Array,
      default: () => [],
    },
    newEventModal: {
      type: Boolean,
      default: true,
    },
    organizationSlug: String,
    ticketSellers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const isArtKinoOrganization = this.organizationSlug === 'art-kino-2599'
    const artKinoPerformanceTypes = ['REGULAR_SCREENING', 'MATINEE', 'TALK']
    const performanceTypes = isArtKinoOrganization
      ? Object.keys(EventPerformanceType)
      : Object.keys(EventPerformanceType).filter(key => !artKinoPerformanceTypes.includes(key))

    return {
      form: this.initialValues,
      openCreateTicketModal: false,
      expanded: !this.soldOut,
      promoCodeNames: [],
      snackbar: {
        open: false,
        message: '',
        color: 'primary',
      },
      performanceTypes,
      selectedTicketSellers: this.initialValues ? this.initialValues.$model.ticketSellerIds : [],
      isTisakInTicketSellers: !!this.ticketSellers.find(
        item => item.name.toLowerCase() === tisakTicketSeller,
      ),
    }
  },
  computed: {
    ticketTypeItems: function () {
      return this.ticketTypes.filter(
        ticket => ticket.venueId === (parseInt(this.form.$model.venueId) || null),
      )
    },
    categorizePromoCodes: function () {
      const categorizedPromoCodes = []
      this.promoCodes.map(pc => {
        const existingCategory = categorizedPromoCodes.find(cpc => cpc.name === pc.name)
        if (!existingCategory) {
          categorizedPromoCodes.push(pc.name)
        }
      })

      return categorizedPromoCodes
    },
    promoCodesAreSelectable: function () {
      const venueRegularTicket = this.ticketTypeItems.find(
        ticket => !ticket.membership && !ticket.ticketSeller && ticket.prepaidCoupon,
      )

      return (
        !this.form.$model.orderItemTemplateIds ||
        (!!this.form.$model.orderItemTemplateIds.length &&
          !!venueRegularTicket &&
          !this.form.$model.orderItemTemplateIds.includes(venueRegularTicket.id))
      )
    },
    hasSomeTicketSalesChannelsAvailable: function () {
      return (this.form.$model.orderItemTemplateIds || []).some(id => {
        const ticket = this.ticketTypeItems.find(item => item.id === id)

        return ticket && ticket.salesChannel
      })
    },
  },
  watch: {
    hasSomeTicketSalesChannelsAvailable: {
      handler(val) {
        if (!val && this.form.$model.ticketSellerIds) {
          this.$emit('removeSelectedTicketSellers', this.form.$model.id)
          this.selectedTicketSellers = []
        }
      },
    },
  },
  methods: {
    handlePromoCodeClick() {
      return (this.form.$model.promoCodeIds = this.promoCodes
        .map(pc => {
          if (this.promoCodeNames.includes(pc.name)) {
            return pc.id
          }
        })
        .filter(Boolean))
    },
    handleTicketSellerClick() {
      this.form.$model.ticketSellerIds = this.ticketSellers
        .filter(item => this.selectedTicketSellers.includes(item.id))
        .map(item => Number(item.id))
    },
    displayPromoCodes() {
      return !Number.isInteger(Number(this.form.$model.id)) && !!this.promoCodes.length
    },
    prettifyDate(date, time) {
      if (!date) {
        return false
      }
      const onlyDate = moment(date)
      const timeFormat = moment(time, 'HH:mm')

      return onlyDate
        .set({ hour: timeFormat.get('hour'), minute: timeFormat.get('minute') })
        .format('DD.MM.YYYY - HH:mm')
    },
    handleOpenCreateTicketModal() {
      this.openCreateTicketModal = !this.openCreateTicketModal
    },
    setTicketVenueSections(ticketInfo) {
      const selectedVenue = this.venues.find(venue => venue.id == ticketInfo.venueId)

      if (
        selectedVenue.seats &&
        selectedVenue.predefinedSections &&
        (!ticketInfo.venueSection.length || ticketInfo.venueSection.includes('All'))
      ) {
        ticketInfo.venueSection = Object.keys({ ...selectedVenue.predefinedSections })
      }

      return ticketInfo
    },
    async handleCreateTicketType(ticketInfo) {
      this.openCreateTicketModal = !this.openCreateTicketModal
      const orderItemTemplateInput = this.setTicketVenueSections(ticketInfo)
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateInput,
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          message: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          message: 'Successfully created ticket type!',
        }
        this.selected = []
        this.$emit('refetchOrderItemAndVenue')
      }
    },
  },
}
</script>

<style scoped>
.ticket-type-wrapper {
  display: flex;
  justify-content: space-between;
  column-gap: 1.5rem;
}
.performance-wrapper {
  margin-bottom: 32px;
  border: solid 2px #d9d9d9;
  border-radius: 8px;
  max-width: 684px;
}
.title-n-buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f5f5f5;
  padding: 1.5rem;
  border-radius: 4px;
}
.performance-title {
  font-family: 'Raleway';
  font-style: normal;
  font-feature-settings: 'pnum' on, 'lnum' on;
  font-weight: 600;
  font-size: 20px;
  color: #333333;
}
.performance-buttons {
  width: 50%;
  display: flex;
  justify-content: flex-end;
}
.performance-buttons section {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.performance-date {
  width: 100%;
  display: flex;
  justify-content: space-between;
  column-gap: 1.5rem;
}
.performance-details {
  margin-top: 42px;
  display: flex;
  flex-direction: column;
  row-gap: 2rem;
  padding: 0 1.5rem 1.5rem;
}
.not-selectable {
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
}
.material-icons {
  cursor: pointer;
}
.button-style {
  font-weight: 700;
  font-size: 18px;
  font-family: Raleway;
}
.scanner-id {
  color: #666;
  font-family: Source Sans Pro;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
}
.event-applications {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  background: white;
  padding: 10px 4px 12px 16px;
  border-radius: 30px;
}
.reports-wrapper {
  background: white;
  padding: 12px;
  border-radius: 100000px;
}
.delete-wrapper {
  background: white;
  border-radius: 4px 25px 25px 4px;
  padding: 12px 14px 12px 10px;
}
.duplicate-wrapper {
  background: white;
  border-radius: 25px 4px 4px 25px;
  padding: 12px 10px 14px 16px;
}
.tisak-term {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #666666;
  text-indent: 1rem;
  margin-top: -0.75rem;
}
</style>
